
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
import VDataTable from 'vuetify/lib/components/VDataTable/VDataTable';
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
import VPagination from 'vuetify/lib/components/VPagination/VPagination';
import Vue from 'vue';
import { primaryColor } from '@/core/new-crm-ui-colors';

const base = Vue.extend({ mixins: [VDataTable] });
export default base.extend({
    name: 'base-data-table',
    props: {
        dense: {
            type: Boolean,
            default: false
        },
        fixedHeader: {
            type: Boolean,
            default: false
        },
        height: {
            type: String,
            default: ''
        },
        footerProps: {
            type: Object,
            default: () => {
                return {
                    itemsPerPageOptions: [10, 25, 50, 100]
                };
            }
        },
        headerProps: {
            type: Object,
            default: () => ({})
        },
        itemsPerPage: {
            type: Number,
            default: 10
        },
        options: {
            type: Object,
            default: () => ({
                sortBy: ['']
            })
        },
        serverItemsLength: {
            type: Number,
            default: -1 // as per Vuetify documentation, -1 is default for serverItemsLength
        },
        noDataText: {
            type: String,
            default: 'No results'
        },
        hideDefaultFooter: {
            type: Boolean,
            default: false
        },
        disablePagination: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            default: () => []
        },
        disableSort: {
            type: Boolean,
            default: false
        },
        headers: {
            type: Array as () => Array<{
                text: string;
                value: string;
                sortable?: boolean;
                [key: string]: any;
            }>,
            default: () => []
        },
        showSelect: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            internalOptions: {
                sortBy: [],
                sortDesc: [],
                page: 1,
                itemsPerPage: this.itemsPerPage,
                ...this.options
            },
            internalFilteredItems: [] as any[]
        };
    },

    watch: {
        options: {
            deep: true,
            handler(newVal) {
                this.internalOptions = {
                    sortBy: [],
                    sortDesc: [],
                    page: 1,
                    itemsPerPage: this.itemsPerPage,
                    ...newVal
                };
            }
        },
        itemsPerPage(newVal) {
            this.internalOptions.itemsPerPage = newVal;
        }
    },

    computed: {
        totalPages(): number {
            const totalItems = this.serverItemsLength === -1 ? this.items.length : this.serverItemsLength;
            return Math.ceil(totalItems / this.internalOptions.itemsPerPage);
        },
        currentItemsPerPage(): number {
            return this.internalOptions.itemsPerPage;
        },
        paginationLabel(): string {
            const totalItems = this.serverItemsLength !== -1 ? this.serverItemsLength : this.items.length;
            if (totalItems === 0) return '0–0 of 0';
            const start = ((this.internalOptions.page - 1) * this.internalOptions.itemsPerPage) + 1;
            const end = Math.min(this.internalOptions.page * this.internalOptions.itemsPerPage, totalItems);
            return `${start}–${end} of ${totalItems}`;
        },
        filteredItems(): any[] {
            // Try to get filtered items from the internal VDataTable
            const internalTable = this.$refs.internalDataTable as any;
            if (internalTable && internalTable.filteredItems) {
                return internalTable.filteredItems;
            }
            // Fallback to our internal storage
            return this.internalFilteredItems;
        }
    },

    mounted() {
        // Initialize filtered items with all items
        this.internalFilteredItems = this.items || [];
    },

    methods: {
        getSortIcon(column: string): string {
            const header = this.headers.find((h: any) => h.value === column);
            if (!header || header.sortable === false) return '';
            const sortIndex = this.internalOptions.sortBy.indexOf(column);
            const isSorted = sortIndex !== -1;
            const isDesc = this.internalOptions.sortDesc?.[sortIndex];

            if (!isSorted) {
                return 'fa-solid fa-sort text-grey';
            }
            if (isDesc) {
                return 'fa-solid fa-sort-down text-black';
            }
            return 'fa-solid fa-sort-up text-black';
        },

        handleSortChange(sortBy: string[], sortDesc: boolean[]) {
            this.internalOptions.sortBy = sortBy;
            this.internalOptions.sortDesc = sortDesc;
            this.$emit('update:options', {
                ...this.internalOptions,
                sortBy,
                sortDesc
            });
        },

        updateFilteredItems(filteredItems: any[]) {
            this.internalFilteredItems = filteredItems;
        },

        getFilteredItems(): any[] {
            return this.internalFilteredItems;
        }
    },

    render(this: Vue & any, h) {
        const currentPage = this.internalOptions.page || 1;
        const totalPages = this.totalPages || 1;

        const headerSlots = Object.fromEntries(
            this.headers.map((header: any) => [
                `header.${header.value}`,
                (slotProps: any) => {
                    // Determine if a sort icon should be shown for this column
                    const showSortIcon =
                        !this.disableSort &&
                        !!slotProps.header.text?.trim() &&
                        header.sortable !== false;

                    // Create the sort icon as a virtual node
                    const sortIcon = showSortIcon
                        ? h('i', {
                            class: ['pl-1', this.getSortIcon(header.value)],
                            style: 'font-size: 0.75rem'
                        })
                        : null;

                    // Check if the parent component provided a custom template for this header
                    if (this.$scopedSlots[`header.${header.value}`]) {
                        // If yes, render the parent's custom content AND our sort icon together.
                        return h('div', { class: 'd-flex align-center' }, [
                            this.$scopedSlots[`header.${header.value}`](slotProps),
                            sortIcon
                        ]);
                    }
                    const isSorted = this.internalOptions.sortBy.includes(header.value);
                    const sortIndex = this.internalOptions.sortBy.indexOf(header.value);
                    const isDesc = this.internalOptions.sortDesc?.[sortIndex];
                    const ariaSort = isSorted ? (isDesc ? 'descending' : 'ascending') : 'none';

                    return h('div', {
                        class: 'd-flex align-center sortable-header',
                        attrs: {
                            role: 'button',
                            tabindex: 0,
                            'aria-label': `Sort by ${header.text}, current order: ${ariaSort}`,
                            'aria-sort': ariaSort
                        },
                        on: {
                            keydown: (e: KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    const newSortDesc = isSorted ? !isDesc : false;
                                    this.handleSortChange([header.value], [newSortDesc]);
                                }
                            }
                        }
                    }, [
                        h('span', slotProps.header.text),
                        sortIcon
                    ]);
                }
            ])
        );

        return h('div', [
            h(
                VDataTable,
                {
                    ref: 'internalDataTable',
                    props: {
                        ...this.$props,
                        options: this.internalOptions,
                        headers: this.headers,
                        items: this.items,
                        search: this.search,
                        itemsPerPage: this.itemsPerPage,
                        noDataText: this.noDataText,
                        disablePagination: this.disablePagination,
                        disableSort: this.disableSort,
                        dense: this.dense,
                        fixedHeader: this.fixedHeader,
                        height: this.height,
                        footerProps: {
                            hideDefaultFooter: true,
                            disablePagination: true
                        },
                        showSelect: this.showSelect
                    },
                    on: {
                        ...this.$listeners,
                        'update:options': (newOptions: any) => {
                            this.internalOptions = newOptions;
                            this.$emit('update:options', newOptions);
                        },
                        'update:sortBy': (sortBy: string[]) => {
                            this.handleSortChange(sortBy, this.internalOptions.sortDesc);
                        },
                        'update:sortDesc': (sortDesc: boolean[]) => {
                            this.handleSortChange(this.internalOptions.sortBy, sortDesc);
                        },
                        'current-items': (items: any[]) => {
                            this.updateFilteredItems(items);
                        }
                    },
                    scopedSlots: {
                        ...this.$scopedSlots,
                        ...headerSlots
                    }
                },
                this.$slots.default
            ),
            !this.hideDefaultFooter &&
                h('div', { class: 'pagination-controller-wrapper' }, [
                    h('div', this.paginationLabel),
                    h('div', [
                        this.$slots.pagination ||
                            h(VPagination, {
                                props: {
                                    length: totalPages,
                                    value: currentPage,
                                    totalVisible: 7,
                                    color: primaryColor,
                                    disabled: this.disablePagination
                                },
                                on: {
                                    input: (val: number) => {
                                        this.internalOptions.page = val;
                                        this.$emit('update:options', {
                                            ...this.internalOptions,
                                            page: val
                                        });
                                    }
                                }
                            })
                    ]),
                    h('de-emphasized-select', {
                        props: {
                            items: this.footerProps.itemsPerPageOptions.map((val: number) => ({
                                label: `${val} / Page`,
                                value: val
                            })),
                            value: this.currentItemsPerPage,
                            itemValue: 'value',
                            itemText: 'label'
                        },
                        on: {
                            input: (val: number) => {
                                this.internalOptions.itemsPerPage = val;
                                this.internalOptions.page = 1;
                                this.$emit('update:options', {
                                    ...this.internalOptions,
                                    itemsPerPage: val,
                                    page: 1
                                });
                            }
                        }
                    })
                ])
        ]);
    }
});
// Number columns can be right aligned by setting align: 'end' for the DataTableHeader entry.
// Columns can be set to not sortable by setting sortable: false for the DataTableHeader entry.
// Columns can be set to not filterable by setting filterable: false for the DataTableHeader entry.
// To add the search box to a table, add a v-text-field with v-model equal to the same property
// set to :search on the table
